// src/components/reports/VaccineInventoryChart.js
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const VaccineInventoryChart = ({ data, warehouseOptions }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
  };

  // กรองคลังที่มีข้อมูลใน data
  const availableWarehouses = [
    ...new Set(data.map((item) => item.WarehouseName)),
  ].filter(Boolean); // กรองค่าที่ไม่ใช่ null/undefined
  const filteredWarehouseOptions = warehouseOptions.filter(
    (warehouse) =>
      warehouse.WarehouseName &&
      availableWarehouses.includes(warehouse.WarehouseName)
  );

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    if (!selectedWarehouse) {
      // รวมยอดทุกคลังตาม VaccineNameTH
      const groupedData = data.reduce((acc, item) => {
        const vaccineName = item.VaccineNameTH;
        if (!acc[vaccineName]) {
          acc[vaccineName] = {
            name: vaccineName,
            quantity: 0,
            vaccineEN: item.VaccineNameEN,
            lotIDs: new Set(),
          };
        }
        acc[vaccineName].quantity += item.TotalQuantity;
        acc[vaccineName].lotIDs.add(item.LotID);
        return acc;
      }, {});

      return Object.values(groupedData).map((item) => ({
        name: item.name,
        quantity: item.quantity,
        vaccineEN: item.vaccineEN,
        lotID: Array.from(item.lotIDs).join(", "),
        warehouse: "ทุกคลัง",
      }));
    } else {
      // กรองข้อมูลตามคลังที่เลือก
      return data
        .filter((item) => item.WarehouseName === selectedWarehouse)
        .map((item) => ({
          name: item.VaccineNameTH,
          quantity: item.TotalQuantity,
          vaccineEN: item.VaccineNameEN,
          lotID: item.LotID,
          warehouse: item.WarehouseName,
        }));
    }
  };

  // กำหนดสีสำหรับกราฟ
  const barColor = "#16A085";

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>ชื่อวัคซีน (TH):</strong> {label}
          </p>
          <p>
            <strong>ชื่อวัคซีน (EN):</strong> {data.vaccineEN}
          </p>
          <p>
            <strong>ชื่อคลัง:</strong> {data.warehouse}
          </p>
          <p>
            <strong>Lot No.:</strong> {data.lotID}
          </p>
          <p>
            <strong>จำนวนคงคลัง:</strong> {data.quantity}
          </p>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >        
     <StandardHeader
          title="กราฟปริมาณวัคซีนคงคลัง"
          bgColor="iGreen.500"
          icon={faChartBar}
        />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกคลัง
        </FormLabel>
        {filteredWarehouseOptions.length > 0 ? (
          <Select
            value={selectedWarehouse}
            onChange={handleWarehouseChange}
            placeholder="ทุกคลัง"
            colorScheme="iBlue"
          >
            {filteredWarehouseOptions.map((warehouse) => (
              <option
                key={warehouse.WarehouseID}
                value={warehouse.WarehouseName}
              >
                {warehouse.WarehouseName}
              </option>
            ))}
          </Select>
        ) : (
          <Text color="gray.500">ไม่มีคลังที่มีข้อมูล</Text>
        )}
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวนคงคลัง",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="quantity"
              name="จำนวนคงคลัง"
              fill={barColor}
              barSize={50}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineInventoryChart;